import { APP_CONFIG } from "../constants";
import { AppError } from "../utils/errorHandler";

function authHeaders(json = false): HeadersInit {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

async function parseError(res: Response): Promise<never> {
  let message = "Erro na requisição";
  try {
    const body = await res.json();
    message = body.message || body.error || message;
  } catch {
    /* ignore */
  }
  throw new AppError(message, res.status);
}

export type BackupFileInfo = {
  available: boolean;
  filename: string | null;
  sizeBytes: number | null;
  sizeHuman: string | null;
  modifiedAt: string | null;
  ageHours: number | null;
  dumpedAt?: string | null;
  syncedAt?: string | null;
  sourceHost?: string | null;
  engine?: string | null;
};

export type BackupDiskInfo = {
  path: string;
  totalBytes: number | null;
  freeBytes: number | null;
  usedBytes: number | null;
  usedRatio: number | null;
};

export type BackupStatus = {
  backupsDirConfigured: boolean;
  backupsDir: string;
  postgres: BackupFileInfo;
  notes: {
    schedule: string;
    retention: string;
    localSync: string;
  };
  storage: {
    appDisk: BackupDiskInfo | null;
    dbDisk?: BackupDiskInfo | null;
  };
};

export type MonitoringSnapshot = {
  hostRootConfigured: boolean;
  hostRootMounted: boolean;
  note: string;
  cpu: {
    usedRatio: number | null;
    cores: number | null;
    sampleMs: number;
    loadPerCore: number | null;
  } | null;
  memory: {
    totalBytes: number | null;
    availableBytes: number | null;
    usedBytes: number | null;
    usedRatio: number | null;
  } | null;
  loadavg: { load1: number; load5: number; load15: number } | null;
  disks: Array<{
    label: string;
    path: string;
    totalBytes: number;
    freeBytes: number;
    usedBytes: number;
    usedRatio: number | null;
  }>;
  dirs: Array<{
    label: string;
    path: string;
    exists: boolean;
    sizeBytes: number | null;
  }>;
  hostname: string | null;
  uptimeSeconds: number;
  nodeVersion: string;
};

export type DesenvolvedorCronHealth =
  | "ok"
  | "stale"
  | "missing"
  | "unknown"
  | "disabled";

export type DesenvolvedorCronJob = {
  id: string;
  name: string;
  description: string;
  host: "app" | "api" | "db";
  schedule: string;
  cronExpression: string;
  timezone: string;
  enabled: boolean;
  health: DesenvolvedorCronHealth;
  lastEvidenceAt: string | null;
  lastEvidenceDetail: string | null;
  maxAgeHours: number | null;
};

export type DesenvolvedorCronsResponse = {
  timezone: string;
  generatedAt: string;
  jobs: DesenvolvedorCronJob[];
};

export const servidorOpsService = {
  getBackupStatus: async (): Promise<BackupStatus> => {
    const res = await fetch(`${APP_CONFIG.API_URL}/desenvolvedor/backups/status`, {
      headers: authHeaders(),
    });
    if (!res.ok) return parseError(res);
    return res.json();
  },

  validateBackup: async () => {
    const res = await fetch(
      `${APP_CONFIG.API_URL}/desenvolvedor/backups/postgres/validate`,
      { method: "POST", headers: authHeaders(true) },
    );
    if (!res.ok) return parseError(res);
    return res.json() as Promise<{
      ok: boolean;
      checkedAt: string;
      detail: string;
      entryCount?: number;
    }>;
  },

  downloadPostgresBackup: async (): Promise<void> => {
    const res = await fetch(
      `${APP_CONFIG.API_URL}/desenvolvedor/backups/postgres/latest`,
      { headers: authHeaders() },
    );
    if (!res.ok) return parseError(res);
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const match = /filename="?([^";]+)"?/.exec(cd);
    const filename = match?.[1] || "dom_belloni-latest.dump";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  getMonitoringSnapshot: async (): Promise<MonitoringSnapshot> => {
    const res = await fetch(
      `${APP_CONFIG.API_URL}/desenvolvedor/monitoring/snapshot`,
      { headers: authHeaders() },
    );
    if (!res.ok) return parseError(res);
    return res.json();
  },

  getCrons: async (): Promise<DesenvolvedorCronsResponse> => {
    const res = await fetch(`${APP_CONFIG.API_URL}/desenvolvedor/crons`, {
      headers: authHeaders(),
    });
    if (!res.ok) return parseError(res);
    return res.json();
  },
};
